<script setup lang="ts">
  import { useAppToast } from '~/composables/shared/useAppToast';
  import { stores } from '~/store';
  import { useFriendsStore } from '~/store/friends';

  const friendsStore = useFriendsStore();
  const { showToast } = useAppToast();

  const EAddFriendsTab = {
    SEARCH: 'search username',
    LAST_PLAYED: 'last played with',
  } as const;

  const friendsListCategoriesTitles = Object.values(EAddFriendsTab);
  const addFriendsTab = ref<'search username' | 'last played with'>('search username');
  const selectedUser = ref('');
  const loading = ref(false);
  const lastPlayedWith = computed(() => stores.firebase.lastPlayedUsers || []);

  const possibleFriends = shallowRef<string[]>([]);
  const search = ref('');

  const lastSearch = ref('');
  const updateSearch = debounce(async (event: Event) => {
    search.value = (event.target as HTMLInputElement).value;

    if (selectedUser.value && selectedUser.value !== lastSearch.value) {
      lastSearch.value = selectedUser.value;
      search.value = selectedUser.value;
    }

    if (search.value !== lastSearch.value) {
      const blockedFrom = friendsStore.blockedFriends;
      const friends = friendsStore.friends;
      const blockedAndFriendsDisplayNames = [...blockedFrom, ...friends].map((a: IFriendData) => a.displayName);
      const usersInProcess = [
        ...friendsStore.sentFriends.map((a: IFriendData) => a.displayName),
        ...friendsStore.receivedFriends.map((a: IFriendData) => a.displayName),
      ].filter(Boolean);

      const currentUserDN = stores.user.profileData?.displayName;
      const searchString = search.value?.trim() || '';
      const excludeUsers = [
        ...blockedAndFriendsDisplayNames.filter((a: string) => a.toLowerCase().includes(searchString)),
        ...usersInProcess.filter((a: string) => a.toLowerCase().includes(searchString)),
        currentUserDN,
      ] as string[];

      selectedUser.value = '';
      lastSearch.value = search.value;
      possibleFriends.value = (await friendsStore.searchUserByName(
        searchString?.replaceAll(' ', ''),
        excludeUsers,
      )) as string[];
    }

    loading.value = false;
  }, 300);

  function setFriendsTab(item: typeof addFriendsTab.value) {
    addFriendsTab.value = item;
  }

  async function addFriend(displayName: string) {
    if (!displayName) return;

    const friendsStore = useFriendsStore();
    const response = await friendsStore.sendFriendRequest(displayName);

    selectedUser.value = '';
    search.value = '';

    const isError = response.status === 'ERROR';
    const title = isError ? 'Error' : 'Success';
    const text = isError ? response.message : 'Your friend request was sent successfully!';

    showToast({
      title,
      description: text,
      color: isError ? 'error' : 'success',
    });
  }
</script>

<template>
  <div
    class="bg-[hsla(0,0%,100%,0.07)] rounded-xl left flex justify-center px-0 pt-0 w-full max-w-[400px] max-h-[400px] md:max-h-none h-full transition-opacity"
  >
    <div class="flex flex-col w-full h-full overflow-y-hidden">
      <GameFriendsListCardHeader title="ADD FRIENDS">
        <template #icon>
          <SvgoAddFriendIcon class="size-10" />
        </template>
      </GameFriendsListCardHeader>

      <div class="space-y-4 py-4">
        <div class="flex px-3 gap-2">
          <CoreButton
            v-for="key in friendsListCategoriesTitles"
            :key="key"
            :class="
              addFriendsTab === key
                ? 'bg-primary hover:bg-primary active:bg-primary after:border-t-[10px]'
                : 'bg-white/5 hover:bg-white/5 active:bg-white/5'
            "
            class="flex-1 h-[60px] relative after:content-[''] after:absolute after:left-[42%] after:top-full after:w-0 after:h-0 after:border-l-[10px] after:border-l-transparent after:border-r-[10px] after:border-r-transparent after:border-t-[#2b7fff] after:clear-both"
            @click="setFriendsTab(key)"
          >
            <span class="text-sm md:text-[18px] font-bold uppercase whitespace-pre-wrap py-3">
              {{ key }}
            </span>
          </CoreButton>
        </div>

        <div v-if="addFriendsTab === EAddFriendsTab.SEARCH" class="px-4">
          <UInputMenu
            v-model="search"
            :loading="loading"
            :items="possibleFriends"
            class="w-full text-white"
            placeholder="Search..."
            icon="i-lucide-search"
            :ui="{
              base: '!text-white h-11 w-full border-0 ring-0 rounded-lg font-semibold font-barlow text-[#a19caa] pl-12',
              leading: 'leading',
              group: 'text-white friend-group py-2 px-0',
              item: 'font-barlow text-white text-[14px] h-12 data-highlighted:not-data-disabled:before:bg-white/8 data-highlighted:not-data-disabled:text-white',
            }"
            @input="updateSearch"
            @update:model-value="addFriend"
          >
            <template v-if="selectedUser" #trailing>
              <UButton
                icon="i-heroicons-plus"
                color="green"
                variant="solid"
                size="xs"
                @click="addFriend(selectedUser)"
              />
            </template>

            <template #item="{ item }">
              <div class="w-full flex items-center justify-between cursor-pointer px-4">
                <span>{{ item }}</span>
                <CoreButton class="h-8">
                  <span class="text-base font-semibold">Add Friend</span>
                </CoreButton>
              </div>
            </template>
          </UInputMenu>
        </div>

        <div
          v-else-if="addFriendsTab === EAddFriendsTab.LAST_PLAYED"
          class="overflow-y-auto max-h-[300px] py-1 px-3 mt-2"
        >
          <ul class="space-y-2 pb-22 md:pb-4">
            <li
              v-if="!lastPlayedWith.length"
              class="text-white text-center font-size-20 font-bold py-3 bg-white/5 rounded-xl"
            >
              Empty
            </li>
            <li
              v-for="(child, index) in lastPlayedWith"
              :key="index"
              class="flex items-center px-4 py-1 rounded-lg bg-white/5"
            >
              <span class="text-white font-bold text-lg">
                {{ child.displayName || child.name }}
              </span>
              <div class="ml-auto">
                <UButton icon="i-heroicons-plus" color="primary" size="xs" @click="addFriend(child.displayName)" />
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
